import dayjs from "dayjs";
import isEqual from "lodash.isequal";
import { computed, makeAutoObservable, reaction } from "mobx";
import { StateSnapshot } from "react-virtuoso";
import { API, Channel, Message, Nullable } from "revolt.js";
import { decodeTime } from "ulid";

import { getApplicationState } from "../../mobx/State";
import { QueuedMessage } from "../../mobx/stores/MessageQueue";

import { getClientController } from "../../controllers/client/ClientController";
import { ChannelRenderer } from "./Singleton";

// import { StateSnapshot } from "react-virtuoso";

type MessageData<Kind extends string, Data = void> = Data extends void
    ? {
          kind: Kind;
          key: string;
          closest_message_id?: string;
      }
    : {
          kind: Kind;
          data: Data;
          key: string;
          closest_message_id?: string;
      };

export type MessageRenderData =
    | MessageData<
          "simple",
          { message: Message; queued?: QueuedMessage; head?: boolean }
      >
    | MessageData<"start", Channel>
    | MessageData<"required-online">
    | MessageData<"divider", { date?: string; unread?: boolean }>
    | MessageData<"blocked", { count: number }>
    | MessageData<"system", Message>
    | MessageData<"nothing-here">;

const VIRTUAL_LIST_SIZE = 1000000;

let id = 0;

export class RenderingProxy {
    firstItemIndex = VIRTUAL_LIST_SIZE;

    initialTopMostItemIndex = VIRTUAL_LIST_SIZE;
    id = id++;

    _fetchedTop = true;

    _snapshot: StateSnapshot | undefined;

    last_id?: string;
    constructor(public renderer: ChannelRenderer) {
        makeAutoObservable(this, {
            _fetchedTop: false,
            _snapshot: false,
            renderData: computed.struct,
        });

        reaction(
            () => this.renderData.length,
            (currentLength) => {
                if (this._fetchedTop) {
                    this.firstItemIndex = VIRTUAL_LIST_SIZE - currentLength + 1;
                }
            },
            { fireImmediately: true }, // Считаем стартовый индекс сразу при создании прокси
        );
    }

    setLastId(lastId?: string) {
        this.last_id = lastId;
    }

    get renderData() {
        const renderer = this.renderer;
        const pinned = renderer.isPinned;
        const last_id = this.last_id;

        const state = getApplicationState();
        const queue = state.queue;
        const client = getClientController().availableClient;
        const userId = client.user!._id;
        const renderData: MessageRenderData[] = [];

        let previous: Message | undefined;

        if (!pinned) {
            if (renderer.atTop === true) {
                renderData.push({
                    key: "start",
                    kind: "start",
                    data: renderer.channel,
                });
            }
        }

        let head = true;
        let divided = false;
        function compare(
            current: string,
            curAuthor: string,
            currentMasq: Nullable<API.Masquerade>,
            previous: string,
            prevAuthor: string,
            previousMasq: Nullable<API.Masquerade>,
        ) {
            head = false;
            const atime = decodeTime(current),
                adate = new Date(atime),
                btime = decodeTime(previous),
                bdate = new Date(btime);

            let unread = false;
            if (!divided && last_id && previous >= last_id) {
                unread = true;
                divided = true;
            }

            let date;
            if (
                adate.getFullYear() !== bdate.getFullYear() ||
                adate.getMonth() !== bdate.getMonth() ||
                adate.getDate() !== bdate.getDate()
            ) {
                date = adate;
            }

            if (unread || date) {
                renderData.push({
                    key: `${current}-${previous}-divider`,
                    kind: "divider",
                    data: {
                        date: date ? dayjs(date).format("LL") : undefined,
                        unread,
                    },
                });
                head = true;
            }

            head =
                head ||
                curAuthor !== prevAuthor ||
                Math.abs(btime - atime) >= 420000 ||
                !isEqual(currentMasq, previousMasq);
        }

        let blocked = 0;
        function pushBlocked(id: string) {
            renderData.push({
                key: `${id}-blocked-${blocked}`,
                kind: "blocked",
                data: { count: blocked },
            });
            blocked = 0;
        }

        for (const message of renderer.messages) {
            if (pinned && !message.pinned) {
                continue;
            }

            if (previous) {
                compare(
                    message._id,
                    message.author_id,
                    message.masquerade,
                    previous._id,
                    previous.author_id,
                    previous.masquerade,
                );
            }

            if (message.author_id === "00000000000000000000000000") {
                renderData.push({
                    key: `system.${message._id}`,
                    kind: "system",
                    data: message,
                });
            } else if (message.author?.relationship === "Blocked") {
                blocked++;
            } else {
                if (blocked > 0) {
                    pushBlocked(message._id);
                }
                renderData.push({
                    key: message._id,
                    kind: "simple",
                    data: { message, head },
                });
            }

            previous = message;
        }

        if (blocked > 0)
            pushBlocked(
                renderData[renderData.length - 1]?.key || "unknown-message-id",
            );

        const nonces = renderer.messages.map((x) => x.nonce);
        for (const msg of queue.get(renderer.channel._id)) {
            if (nonces.includes(msg.id)) continue;

            if (previous) {
                compare(
                    msg.id,
                    userId!,
                    null,
                    previous._id,
                    previous.author_id,
                    previous.masquerade,
                );

                previous = {
                    _id: msg.id,
                    author_id: userId!,
                } as Message;
            }

            renderData.push({
                kind: "simple",
                key: msg.id,
                data: {
                    message: new Message(client, {
                        ...msg.data,
                        replies: msg.data.replies.map((x) => x.id),
                    }),
                    queued: msg,
                },
            });
        }

        if (renderData.length === 0 && pinned) {
            renderData.push({
                key: "nothing-here",
                kind: "nothing-here",
            });
        }

        return renderData;
    }

    init(messageId?: string) {
        if (messageId && this.isMessageLoaded(messageId)) {
            return;
        }
        this.firstItemIndex = VIRTUAL_LIST_SIZE;
        this._fetchedTop = true;
        return this.renderer.init(messageId);
    }

    isMessageLoaded(messageId: string) {
        for (const msg of this.renderer.messages) {
            if (msg._id === messageId) {
                return true;
            }
        }

        return false;
    }

    fetchTop() {
        this._fetchedTop = true;
        this.renderer.loadTop();
    }

    fetchBottom() {
        this._fetchedTop = false;
        this.renderer.loadBottom();
    }

    normalizeIndex(index: number) {
        return VIRTUAL_LIST_SIZE + this.renderData.length - index;
    }

    findMessageIndexById(messageId: string) {
        const idx = this.renderData.findIndex((item) => item.key === messageId);
        return idx;
    }
}
